import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { bookList, categories, getBookTitleParts, scrollChildIntoCenter } from './utils';
import { ImageO } from './ImageO';
import useTilt from './useTilt';

const IS_PROD = process.env.NODE_ENV === 'production';
const BOOKS_BASE_URL = IS_PROD ? '' : (process.env.REACT_APP_BOOKS_URL || '');

const bookIndexByName = new Map(bookList.map((book, index) => [book.name, index]));

function getSortableTitle(title) {
  return title.replace(/^(The|A|An)\s+/i, '');
}

function getYear(dateStr) {
  if (!dateStr) return 0;
  if (dateStr.startsWith('-')) {
    return -parseInt(dateStr.slice(1, 5), 10);
  }
  return parseInt(dateStr.slice(0, 4), 10);
}

function BooksList({ compact, selectedBookName, onBookSelect, onBooksLoaded, hideIntro }) {
  const [books, setBooks] = useState(IS_PROD ? bookList : []);
  const [loading, setLoading] = useState(!IS_PROD);
  const [sortBy, setSortBy] = useState('title');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(!compact);
  const [tiltBookName, setTiltBookName] = useState(null);
  const controlsRef = useRef(null);
  const scrollClipRef = useRef(null);
  const {
    ref: tiltRef,
    bgRef: tiltBgRef,
    fgRef: tiltFgRef,
    onMouseMove: tiltOnMouseMove,
    onMouseEnter: tiltOnMouseEnter,
    onMouseLeave: tiltOnMouseLeave,
    resetTilt,
  } = useTilt(12, compact ? 8 : 15);

  const selectedBook =
    books.find((book) => book.name === selectedBookName) ||
    bookList.find((book) => book.name === selectedBookName);
  const selectedIsComingSoon = !!selectedBook && !selectedBook.prodReady;
  const comingSoonExpanded = showComingSoon || (compact && selectedIsComingSoon);

  useEffect(() => {
    if (!compact) {
      setShowComingSoon(true);
      return;
    }
    setShowComingSoon(selectedIsComingSoon);
  }, [compact, selectedIsComingSoon]);

  const handleCompactHoverIn = useCallback(() => {
    if (!compact || !selectedBookName) return;
    const clip = scrollClipRef.current;
    if (!clip) return;
    const selected = clip.querySelector('.book-card-container-selected');
    if (!selected) return;
    scrollChildIntoCenter(clip, selected);
  }, [compact, selectedBookName]);

  useEffect(() => {
    setTiltBookName(null);
    tiltBgRef.current = null;
    tiltFgRef.current = null;
    resetTilt();
  }, [compact, sortBy, sortDir, selectedCategories, resetTilt, tiltBgRef, tiltFgRef]);

  const activateCardTilt = useCallback((event, bookName) => {
    const tiltEl = event.currentTarget;
    setTiltBookName(bookName);
    tiltRef.current = tiltEl;
    tiltBgRef.current = tiltEl.querySelector('.book-card-bg');
    tiltFgRef.current = tiltEl.querySelector('.book-card-fg');
    tiltOnMouseEnter();
  }, [tiltOnMouseEnter, tiltRef, tiltBgRef, tiltFgRef]);

  const deactivateCardTilt = useCallback(() => {
    setTiltBookName(null);
    tiltBgRef.current = null;
    tiltFgRef.current = null;
    tiltOnMouseLeave();
  }, [tiltOnMouseLeave, tiltBgRef, tiltFgRef]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (controlsRef.current && !controlsRef.current.contains(e.target)) {
        setShowSortMenu(false);
        setShowFilterMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    async function fetchBooks() {
      try {
        if (IS_PROD) {
          onBooksLoaded?.(bookList);
        } else if (BOOKS_BASE_URL) {
          const response = await fetch(`${BOOKS_BASE_URL}/api/books/list`);
          const booksData = await response.json();
          setBooks(booksData);
          onBooksLoaded?.(booksData);
        } else {
          const response = await fetch('/api/books/list');
          const booksData = await response.json();
          setBooks(booksData);
          onBooksLoaded?.(booksData);
        }
      } catch (error) {
        console.error('Error fetching books:', error);
        setBooks(bookList);
        onBooksLoaded?.(bookList);
      } finally {
        setLoading(false);
      }
    }

    fetchBooks();
  }, [onBooksLoaded]);

  const filteredBooks = useMemo(() => (
    selectedCategories.length > 0
      ? books.filter(book => selectedCategories.some(cat => book.category?.includes(cat)))
      : books
  ), [books, selectedCategories]);

  const { readyBooks, comingSoonBooks } = useMemo(() => {
    const sortBooks = (list) => {
      const sorted = [...list].sort((a, b) => {
        switch (sortBy) {
          case 'author':
            return a.author.localeCompare(b.author);
          case 'date':
            return getYear(a.publishDate) - getYear(b.publishDate);
          case 'title':
          default:
            return getSortableTitle(a.title).localeCompare(getSortableTitle(b.title));
        }
      });
      return sortDir === 'desc' ? sorted.reverse() : sorted;
    };

    return {
      readyBooks: sortBooks(filteredBooks.filter((book) => book.prodReady)),
      comingSoonBooks: sortBooks(filteredBooks.filter((book) => !book.prodReady)),
    };
  }, [filteredBooks, sortBy, sortDir]);

  if (loading) {
    const LoadingSurface = compact ? 'div' : 'main';
    return (
      <LoadingSurface className={`books-list-surface ${compact ? 'compact' : 'full'} books-list-surface-loading`}>
        <div className="book-loading">
          <div className="book-loading-spinner" />
        </div>
      </LoadingSurface>
    );
  }

  const isSortedByDate = sortBy === 'date';
  const isSortedByAuthor = sortBy === 'author';
  const isSortedByTitle = sortBy === 'title';

  const renderBookCard = (book, titleElement, priority = false) => {
    const tiltActive = tiltBookName === book.name;

    return (
      <Link
        to={`/${book.name}`}
        className="book-card card"
        onClick={() => onBookSelect(book.name)}
      >
        <div
          className="book-card-tilt"
          onMouseEnter={(event) => activateCardTilt(event, book.name)}
          onMouseLeave={deactivateCardTilt}
          onMouseMove={tiltActive ? tiltOnMouseMove : undefined}
        >
          <div className="book-card-bg always-show">
            <ImageO
              book={book}
              imagePath="book-cover.jpg"
              bookIndex={bookIndexByName.get(book.name) ?? -1}
              showCollage={true}
              priority={priority}
            />
          </div>
          <div className="book-card-fg card">
            <h3 className="book-title" style={{ ...(book.title.length > 20 ? { fontSize: '0.9rem' } : {}) }}>
              <span className="bg-text">{titleElement}</span>
            </h3>
            <p>{book.author}</p>
            {!compact && (
              <div className="book-card-footer">
                <span className="book-card-category">{book.category?.join(', ')}</span>
                <span className="book-card-publish-date">{getYear(book.publishDate) < 0 ? `${Math.abs(getYear(book.publishDate))} BC` : getYear(book.publishDate)}</span>
              </div>
            )}
          </div>
        </div>
      </Link>
    );
  };

  const getFirstLetter = (b) => {
    if (isSortedByAuthor) return b.author[0].toUpperCase();
    if (isSortedByDate) {
      const year = getYear(b.publishDate);
      if (year < 0) {
        const decade = Math.ceil(year / 10) * -10;
        return `${decade}s BC`;
      }
      const decade = Math.floor(year / 10) * 10;
      return `${decade}s`;
    }
    return getSortableTitle(b.title)[0].toUpperCase();
  };

  const renderBookList = (list) => (
    <div className={`book-list ${compact ? 'book-list-compact' : ''} ${isSortedByDate ? 'books-sorted-by-date' : ''}`}>
      {list.map((book, index) => {
        const titleParts = getBookTitleParts(book.title);
        const articleMatch = book.title.match(/^(The|A|An)\s+/i);
        const titleElement = titleParts ? (
          <>{titleParts.main} <small className="book-title-rest">{titleParts.small}</small></>
        ) : articleMatch ? (
          <><small>{articleMatch[0]}</small>{book.title.substring(articleMatch[0].length)}</>
        ) : book.title;

        if (compact) {
          return (
            <div key={book.name} className={`book-card-container${selectedBookName === book.name ? ' book-card-container-selected' : ''}`}>
              {renderBookCard(book, titleElement)}
            </div>
          );
        }

        const firstLetter = getFirstLetter(book);
        const prevFirstLetter = index > 0 ? getFirstLetter(list[index - 1]) : null;
        const showLetter = firstLetter !== prevFirstLetter;

        return (
          <div key={book.name} className={`book-card-container ${selectedBookName === book.name ? 'book-card-container-selected' : ''}`}>
            {showLetter && <div className="letter-header">{firstLetter}</div>}
            {renderBookCard(book, titleElement, index === 0)}
          </div>
        );
      })}
    </div>
  );

  const Surface = compact ? 'div' : 'main';

  return (
    <Surface className={`books-list-surface ${compact ? 'compact' : 'full'}`}>
      {!compact && (
        <div className="books-list-header-area">
          <div className="controls-container" ref={controlsRef}>
            <div className="control-wrapper">
              <button
                type="button"
                className={`control-btn sort-btn ${showSortMenu ? 'active' : ''}`}
                aria-expanded={showSortMenu}
                onClick={() => {
                  setShowSortMenu((open) => !open);
                  setShowFilterMenu(false);
                }}
              >
                {sortBy.charAt(0).toUpperCase() + sortBy.slice(1)}
                <span className={`icon-sort${sortDir === 'desc' ? ' icon-sort-up' : ''}`} />
              </button>
              {showSortMenu && (
                <div className="control-menu">
                  <small>Sort by</small>
                  <button type="button" className={isSortedByTitle ? 'selected' : ''} onClick={() => { setSortBy('title'); setShowSortMenu(false); }}>Title</button>
                  <button type="button" className={isSortedByAuthor ? 'selected' : ''} onClick={() => { setSortBy('author'); setShowSortMenu(false); }}>Author</button>
                  <button type="button" className={isSortedByDate ? 'selected' : ''} onClick={() => { setSortBy('date'); setShowSortMenu(false); }}>Date</button>
                  <div className="control-menu-sort-dir">
                    <button
                      type="button"
                      className={`control-btn control-menu-sort-dir-btn${sortDir === 'asc' ? ' selected' : ''}`}
                      aria-label="Sort ascending"
                      onClick={() => setSortDir('asc')}
                    >
                      <span className="icon-sort" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className={`control-btn control-menu-sort-dir-btn${sortDir === 'desc' ? ' selected' : ''}`}
                      aria-label="Sort descending"
                      onClick={() => setSortDir('desc')}
                    >
                      <span className="icon-sort icon-sort-up" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="control-wrapper">
              <button
                type="button"
                className={`control-btn genre-filter-btn ${showFilterMenu ? 'active' : ''}`}
                aria-expanded={showFilterMenu}
                onClick={() => {
                  setShowFilterMenu((open) => !open);
                  setShowSortMenu(false);
                }}
              >
                <span className="genre-filter-count">{selectedCategories.length > 0 ? `${selectedCategories.length}` : 'All'}</span>
                <div>
                  <span className="icon-genre" />
                </div>
              </button>
              {showFilterMenu && (
                <div className="control-menu control-menu-filter">
                  <small>Filter by genre</small>
                  <button
                    type="button"
                    className={selectedCategories.length === 0 ? 'selected' : ''}
                    onClick={() => setSelectedCategories([])}
                  >
                    All
                  </button>
                  {Object.keys(categories).map((cat) => (
                    <button
                      type="button"
                      key={cat}
                      className={selectedCategories.includes(cat) ? 'selected' : ''}
                      onClick={() => {
                        setSelectedCategories((prev) =>
                          prev.includes(cat)
                            ? prev.filter((c) => c !== cat)
                            : [...prev, cat]
                        );
                      }}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        className="books-layer-scroll-clip"
        ref={scrollClipRef}
        onMouseEnter={compact ? handleCompactHoverIn : undefined}
      >
        <div className="books-list-scroll">
          {!compact && !hideIntro && (
            <p className="books-list-intro-text">Enjoy some of the best books   
            <Link
              to="/about"
              state={selectedBookName ? { from: `/${selectedBookName}` } : undefined}
              className="books-list-intro-icon"
              aria-label="About LoreBites"
            >
                  <img
                    src={`${process.env.PUBLIC_URL}/icons/LoreBiteCompression.svg`}
                    alt=""
                  />
                </Link>   into Bite-sized stanzas.</p>
          )}
          {renderBookList(readyBooks)}
          {comingSoonBooks.length > 0 && (
            <>
              <button
                type="button"
                className={`coming-soon-toggle${comingSoonExpanded ? ' coming-soon-toggle-open' : ''}`}
                aria-expanded={comingSoonExpanded}
                onClick={() => {
                  if (compact && selectedIsComingSoon) return;
                  setShowComingSoon((open) => !open);
                }}
              >
                Coming Soon
                <span className={`caret ${comingSoonExpanded ? 'caret--down' : 'caret--up'}`} />
              </button>
              {comingSoonExpanded && renderBookList(comingSoonBooks)}
            </>
          )}
        </div>
      </div>
    </Surface>
  );
}

export default BooksList;
